import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { type PackAccountsContextItem, type PackMap } from "../types";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { fetchPackLicenseTypes } from "features/Licensing/Packs/state/thunks/packLicenseTypesThunk";
import { connect, type ConnectedProps } from "react-redux";
import { Loader } from "semantic-ui-react";
import { LicenseTypes } from "enums";
import moment from "moment";
import { PackItem } from "./PackItem";
import styles from "./packAccountItems.module.scss";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../featureFlags";
import { reset as resetAvailableDomains } from "features/Accounts/state/slices/accountDomainsSlice";
import { bindAction } from "../../../../interfaces";

interface PackAccountItemsPropsOwn {
  packMap: PackMap;
  setIsDisabled: (value: boolean) => void;
  isEdit?: boolean;
  infoText: string;
}

export type PackAccountItemsProps = PackAccountItemsPropsOwn & PropsFromRedux;

export const PackAccountItems: FC<PackAccountItemsProps> = (props) => {
  const isThreatDefenceDomainEnabled = !!useFeatureFlag(FeatureFlags.ThreatDefenceDomains);
  const {
    packMap,
    setIsDisabled,
    licenseTypes,
    isLoading,
    loadLicenseTypes,
    isEdit,
    infoText,
    selectedDomain,
    resetAvailableDomainsAction,
  } = props;
  const [selectedItems, setSelectedItems] = useState<PackAccountsContextItem[]>(() =>
    [...packMap.values()].map((item) => ({
      ...item,
      ...(item.noExpiration && { licenseTypeId: item.licenseTypeId || LicenseTypes.CustomDate, isChanged: true }),
    })),
  );
  useEffect(() => {
    loadLicenseTypes();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const hasInvalidItems = selectedItems.some(
      (p) => !p.noExpiration && !p.expirationDate && (!p.licenseTypeId || p.licenseTypeId === LicenseTypes.CustomDate),
    );

    const allItemsUnchanged = selectedItems.every((p) => !p.isChanged);

    const hasExpiredTrialItems = selectedItems.some(
      (p) => p.isChanged && p.licenseTypeId === LicenseTypes.Trial && p.licenseInfo?.isExpired,
    );

    const firstThreatDefencePack = selectedItems.find((i) => i.isThreatDefencePack);
    const isTrialSelected = !!firstThreatDefencePack && firstThreatDefencePack?.licenseTypeId !== LicenseTypes.Trial;
    const isDomainInvalid = isThreatDefenceDomainEnabled && isTrialSelected && !selectedDomain.isAssigned;

    const isNotValid =
      !selectedItems.length || hasInvalidItems || allItemsUnchanged || hasExpiredTrialItems || isDomainInvalid;

    setIsDisabled(isNotValid);
  }, [selectedDomain, isThreatDefenceDomainEnabled, selectedItems, setIsDisabled]);

  useEffect(() => {
    if (selectedDomain.packId) {
      const pack = selectedItems.find((i) => i.isThreatDefencePack);
      if (pack?.id !== selectedDomain.packId || pack.licenseTypeId === LicenseTypes.Trial)
        resetAvailableDomainsAction();
    }
  }, [resetAvailableDomainsAction, selectedDomain.packId, selectedItems]);

  const onExpirationDateChange = useCallback(
    (selectedPackId: number, optionId?: number, date?: Date | null) => {
      setSelectedItems((prevPacks) => {
        const packs = [...prevPacks];
        const packIndex = packs.findIndex((el) => el.id === selectedPackId);

        if (packIndex !== -1) {
          const updatedPack = {
            ...packs[packIndex],
            licenseTypeId: optionId ?? packs[packIndex].licenseTypeId,
            expirationDate:
              packs[packIndex].licenseTypeId === LicenseTypes.CustomDate
                ? date ?? packs[packIndex].expirationDate
                : undefined,
            isChanged:
              (optionId && optionId !== packs[packIndex].licenseTypeId) ||
              (date !== undefined && !moment(date).isSame(packs[packIndex].expirationDate, "day")) ||
              packs[packIndex].isChanged,
          };
          packs[packIndex] = updatedPack;

          setSelectedItems(packs);
          packMap.set(updatedPack.id, updatedPack);

          return packs;
        }

        return prevPacks;
      });
    },
    [setSelectedItems, packMap],
  );

  const renderInfoSection = () => {
    const hasNoItems = !selectedItems.length;

    return <div className={styles.info}>{hasNoItems ? "The Customer doesn't have any available packs" : infoText}</div>;
  };

  const firstThreatDefencePackId = useMemo(() => selectedItems.find((i) => i.isThreatDefencePack)?.id, [selectedItems]);

  const shouldShowDomain = useCallback(
    (pack: PackAccountsContextItem) => {
      const isTrialLicenseSelected = pack?.licenseTypeId === LicenseTypes.Trial;
      const isThreatDefencePack = pack?.id === firstThreatDefencePackId && !pack?.isTrial;

      return isThreatDefenceDomainEnabled && isThreatDefencePack && !isTrialLicenseSelected;
    },
    [isThreatDefenceDomainEnabled, firstThreatDefencePackId],
  );

  return (
    <form className={styles.root} autoComplete="off">
      {!isLoading && renderInfoSection()}
      {licenseTypes.length > 0 &&
        selectedItems.map((item, idx) => (
          <PackItem
            key={item.id}
            item={item}
            options={licenseTypes}
            onExpirationDateChange={onExpirationDateChange}
            isEdit={isEdit}
            idx={idx}
            showDomain={shouldShowDomain(item)}
          />
        ))}
      <Loader active={isLoading} />
    </form>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    selectedDomain: state.accounts.availableDomains.selectedDomain,
    licenseTypes: state.packs.packLicenseTypes.items,
    isLoading: state.packs.packLicenseTypes.isLoading,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  loadLicenseTypes: bindAction(fetchPackLicenseTypes, dispatch),
  resetAvailableDomainsAction: bindAction(resetAvailableDomains, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PackAccountItems);
export default ConnectedComponent;
